import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Slide,
  Fade,
  Menu,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KzoLogo from '../../assets/KC-BLANCO.png';
import { useLanguage } from '../../context/LanguageContext';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileDirectorsOpen, setMobileDirectorsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { lang, setLang } = useLanguage();

  const directores = [
    { text: 'MARCELO SZECHTMAN', path: '/directores/marceloszechtman' },
    { text: 'RADA', path: '/directores/rada' },
    { text: 'LEAN MANCINI', path: '/directores/leanmancini' },
    { text: 'MARTINA GIANCATERINO', path: '/directores/martinagiancaterino' },
    { text: 'ROCO CORBOULD', path: '/directores/rococorbould' },
    { text: 'SABRINA CAMPOS', path: '/directores/sabrinacampos' },
    { text: 'JUANI PONCE', path: '/directores/juaniponce' },
  ];

  const menuItems = [
    { text: 'HOME', path: '/' },
    { text: 'FILM', path: '/film' },
    { text: 'AI', path: '/ai' },
    { text: lang === 'en' ? 'CONTACT' : 'CONTACTO', path: '/contacto' },
  ];

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const handleMenuItemClick = (item) => {
    if (item.hasSubmenu) {
      setMobileDirectorsOpen((prev) => !prev);
      return;
    }
    setMobileOpen(false);
    if (item.path) {
      setTimeout(() => navigate(item.path), 300);
    }
  };

  const handleDirectorsMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleDirectorsMenuClose = () => setAnchorEl(null);

  const drawer = (
    <Slide in={mobileOpen} direction="down">
      <List
        sx={{
          background: 'rgba(18, 18, 18, 0.75)',
          backdropFilter: 'blur(20px)',
          height: '100%',
          pt: 8,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'white',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.1)',
              transform: 'rotate(90deg)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <CloseIcon />
        </IconButton>

        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            <Fade in={mobileOpen} timeout={500}>
              <ListItem
                onClick={() => handleMenuItemClick(item)}
                sx={{
                  color: 'white',
                  textDecoration: 'none',
                  py: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    textShadow: '0 0 10px rgba(255, 255, 255, 0.7)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <ListItemText
                  primary={item.text}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: 300,
                      letterSpacing: '2px',
                      fontSize: '1.1rem',
                    },
                  }}
                />
              </ListItem>
            </Fade>

            {item.hasSubmenu && mobileDirectorsOpen && (
              <Fade in={mobileDirectorsOpen} timeout={500}>
                <List sx={{ pl: 2, background: 'rgba(0, 0, 0, 0.2)' }}>
                  {directores.map((director) => (
                    <ListItem
                      key={director.text}
                      component={Link}
                      to={director.path}
                      onClick={handleDrawerToggle}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        textDecoration: 'none',
                        py: 1.5,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          transform: 'translateX(10px)',
                        },
                      }}
                    >
                      <ListItemText
                        primary={director.text}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontWeight: 300,
                            letterSpacing: '1px',
                            fontSize: '0.9rem',
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Fade>
            )}
          </React.Fragment>
        ))}

        <Fade in={mobileOpen} timeout={500}>
          <Box sx={{ display: 'flex', gap: 1, px: 2, mt: 2 }}>
            {[
              { code: 'es', label: 'ES', flag: 'ðŸ‡¦ðŸ‡·' },
              { code: 'en', label: 'EN', flag: 'ðŸ‡ºðŸ‡¸' },
            ].map((opt) => (
              <Box
                key={opt.code}
                onClick={() => {
                  setLang(opt.code);
                  setMobileOpen(false);
                }}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '10px',
                  py: 1.5,
                  cursor: 'pointer',
                  letterSpacing: '2px',
                  transition: 'all 0.3s ease',
                  backgroundColor: lang === opt.code ? 'rgba(255,255,255,0.1)' : 'transparent',
                  fontWeight: lang === opt.code ? 700 : 500,
                  fontSize: lang === opt.code ? '1rem' : '0.95rem',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
               {opt.label}
              </Box>
            ))}
          </Box>
        </Fade>
      </List>
    </Slide>
  );

  const DesktopMenuItem = ({ item }) => (
    <Box
      onMouseEnter={item.hasSubmenu ? handleDirectorsMenuOpen : undefined}
      onClick={item.hasSubmenu ? handleDirectorsMenuOpen : undefined}
      sx={{
        position: 'relative',
        cursor: 'pointer',
      }}
    >
      <Link to={item.path} style={{ textDecoration: 'none' }}>
        <Box
          sx={{
            color: 'white',
            fontWeight: 400,
            letterSpacing: '2px',
            fontSize: '0.9rem',
            fontFamily: 'Delight',
            position: 'relative',
            padding: '8px 0',
            transition: 'all 0.3s ease',
            '&:hover': {
              textShadow: '0 0 10px rgba(255, 255, 255, 0.7)',
              transform: 'translateY(-2px)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '0%',
              height: '1px',
              backgroundColor: 'white',
              transition: 'width 0.3s ease',
            },
            '&:hover::after': {
              width: '100%',
            },
          }}
        >
          {item.text}
        </Box>
      </Link>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: 'rgba(18, 18, 18, 0.2) !important',
        backgroundImage: 'none !important',
        backdropFilter: 'blur(8px)',
        boxShadow: 'none',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        height: isMobile ? '80px' : 'auto',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
        },
      }}
    >
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          py: isMobile ? 2 : 1,
          minHeight: isMobile ? '80px !important' : '64px !important',
          background: 'transparent',
        }}
      >
        {isMobile ? (
          <>
            <Box
              component="img"
              src={KzoLogo}
              alt="Kuarzo"
              onClick={() => navigate('/')}
              sx={{
                height: 60,
                filter: 'brightness(1.2)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': { filter: 'brightness(1.4)' },
              }}
            />
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                color: 'white',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  transform: 'rotate(-90deg)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <MenuIcon />
            </IconButton>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 4 }}>
              {menuItems.map((item) => (
                <DesktopMenuItem key={item.text} item={item} />
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mx: 2, px: 2 }}>
              {[
                { code: 'es', label: 'ES', flag: 'ðŸ‡¦ðŸ‡·' },
                { code: 'en', label: 'EN', flag: 'ðŸ‡ºðŸ‡¸' },
              ].map((opt) => (
                <Box
                  key={opt.code}
                  onClick={() => setLang(opt.code)}
                  sx={{
                    color: 'white',
                    fontWeight: lang === opt.code ? 700 : 500,
                    letterSpacing: '1px',
                    fontSize: lang === opt.code ? '1rem' : '0.85rem',
                    cursor: 'pointer',
                    py: 1,
                    transition: 'all 0.3s ease',
                  }}
                >
                  {opt.label}
                </Box>
              ))}
              <Box
                component="img"
                src={KzoLogo}
                alt="Kuarzo"
                sx={{
                  height: 40,
                  filter: 'brightness(1.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': { filter: 'brightness(1.4)' },
                  ml: 4,
                }}
              />
            </Box>
          </>
        )}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleDirectorsMenuClose}
          MenuListProps={{ onMouseLeave: handleDirectorsMenuClose }}
          PaperProps={{
            sx: {
              mt: 2,
              background: 'rgba(18, 18, 18, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
              '& .MuiMenuItem-root': {
                color: 'white',
                fontSize: '0.9rem',
                letterSpacing: '1px',
                padding: '12px 24px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(5px)',
                },
              },
            },
          }}
        >
          {directores.map((director) => (
            <MenuItem
              key={director.text}
              onClick={handleDirectorsMenuClose}
              component={Link}
              to={director.path}
              sx={{
                fontFamily: 'Delight',
                fontWeight: 400,
              }}
            >
              {director.text}
            </MenuItem>
          ))}
        </Menu>

        <Drawer
          variant="temporary"
          anchor="top"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              background: 'transparent',
              boxShadow: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;

